import { useEffect } from 'react';
import { useAppDispatch } from '@/store';
import { api, refreshSession } from '@/store/api';

type Tag = 'Call' | 'Order' | 'Dashboard';

const TAGS: Record<string, Tag[]> = {
  call: ['Call', 'Dashboard'],
  order: ['Order', 'Dashboard']
};

/** Waits between refetches, so a burst of changes costs one round of requests. */
const DEBOUNCE_MS = 500;
const MAX_RETRY_MS = 30_000;

/**
 * Keeps calls, orders and the overview live. The server's event stream
 * (GET /api/events) only says what changed; the screens on view refetch it
 * through RTK Query as if the operator had pressed refresh. A dropped stream
 * reconnects on its own, and every reconnect refetches once to catch up on
 * anything missed while it was down.
 */
export function useLiveUpdates(enabled: boolean): void {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;

    let source: EventSource | null = null;
    let retryMs = 2_000;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let flushTimer: ReturnType<typeof setTimeout> | undefined;
    let everOpened = false;
    let closed = false;
    const pending = new Set<Tag>();

    const invalidate = (tags: Tag[]) => {
      tags.forEach((tag) => pending.add(tag));
      flushTimer ??= setTimeout(() => {
        flushTimer = undefined;
        dispatch(api.util.invalidateTags([...pending]));
        pending.clear();
      }, DEBOUNCE_MS);
    };

    const connect = () => {
      source = new EventSource('/api/events', { withCredentials: true });

      source.onopen = () => {
        retryMs = 2_000;
        if (everOpened) invalidate(['Call', 'Order', 'Dashboard']);
        everOpened = true;
      };

      source.addEventListener('change', (event) => {
        try {
          const { kinds } = JSON.parse((event as MessageEvent<string>).data) as { kinds: string[] };
          invalidate(kinds.flatMap((kind) => TAGS[kind] ?? []));
        } catch {
          /* a malformed message is ignored; the next one will do */
        }
      });

      source.onerror = () => {
        // The browser retries a dropped stream by itself; it gives up only
        // when the server refuses it (usually an expired access cookie).
        if (source?.readyState !== EventSource.CLOSED || closed) return;
        source = null;
        retryTimer = setTimeout(async () => {
          await refreshSession();
          if (!closed) connect();
        }, retryMs);
        retryMs = Math.min(retryMs * 2, MAX_RETRY_MS);
      };
    };

    connect();

    return () => {
      closed = true;
      source?.close();
      clearTimeout(retryTimer);
      clearTimeout(flushTimer);
    };
  }, [enabled, dispatch]);
}
